"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ProductGallery } from "./product-gallery";
import { ColorImagePreloader } from "./color-image-preloader";
import { useColorPreloadTargets } from "./use-color-preload-targets";
import { getVariantImages, type StickId } from "./variant-images";
import { STICKS } from "./catalog-config";
import type { Product } from "./product-types";
import { useCart } from "@/components/cart/cart-context";
import { formatMoneyWithCode } from "@/lib/currency";

type ColorOption = { id: string; name: string; swatchHex: string };
type ComponentOption = { id: string; name: string };

export function BuyExperience({
  products, colors, components, ignoreStock, colorPreloadGridSteps, colorPreloadSequenceSteps,
}: {
  products: Product[];
  colors: ColorOption[];
  components: ComponentOption[];
  ignoreStock: boolean;
  // Both optional, passed straight through to useColorPreloadTargets (see that file for its own
  // defaults/meaning) -- exposed here too so a caller of BuyExperience can dial background-preloading
  // down (e.g. for a slow/metered-connection mode) without reaching into this component's internals.
  colorPreloadGridSteps?: number;
  colorPreloadSequenceSteps?: number;
}) {
  const [stick, setStick] = useState<StickId>("85");
  const [colorId, setColorId] = useState<string | undefined>(colors[0]?.id);
  // Collapsed by default -- unlike /customize's per-component tiles (which start open since nothing's
  // chosen yet), this page always has a color pre-selected (colors[0]), so there's nothing to prompt.
  const [colorOpen, setColorOpen] = useState(false);
  // Gates ColorImagePreloader -- true only once the CURRENTLY selected color's own visible photo has
  // finished loading (see ProductGallery's onMainImageLoad), and reset back to false the instant a new
  // color is picked so the background preload never starts before that new selection's own image has.
  const [currentImageReady, setCurrentImageReady] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting for a new colorId, same tradeoff as this codebase's other per-selection reset effects
    setCurrentImageReady(false);
  }, [colorId]);
  // WHICH colors to preload -- real geometric grid neighbors while the picker's open (registerSwatchEl
  // needs to be attached to each swatch button below for that to have anything to measure), a flat-array
  // sequence fallback when it's closed. See use-color-preload-targets.ts.
  const { targets: colorPreloadTargets, registerSwatchEl } = useColorPreloadTargets({
    colors, currentColorId: colorId, gridVisible: colorOpen,
    gridSteps: colorPreloadGridSteps, sequenceSteps: colorPreloadSequenceSteps,
  });
  const { addItem } = useCart();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const stickMeta = STICKS.find((s) => s.id === stick)!;
  const selectedColor = colors.find((c) => c.id === colorId) ?? null;
  const images = getVariantImages({ colorName: selectedColor?.name ?? null, stick });

  const selectedProduct = useMemo(
    () => products.find((p) => p.fitment === stickMeta.fitment) ?? null,
    [products, stickMeta]
  );

  // Resolves (or creates) the "Basic" Customization -- every current component set to the one
  // chosen color -- then adds that Selection to the cart. See docs/architecture/
  // commerce-cart-checkout-orders.md § Product customization for why even the simple single-color
  // path goes through the same Customization mechanism as the full per-component /customize flow.
  const buildAndAddToCart = async (options?: { openCartOnAdd?: boolean }): Promise<boolean> => {
    if (!selectedProduct || !selectedColor || components.length === 0) return false;
    setError(null);
    try {
      const customizationResponse = await fetch("/api/customizations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tier: "Basic",
          componentColors: components.map((component) => ({ componentId: component.id, colorId: selectedColor.id })),
        }),
      });
      if (!customizationResponse.ok) {
        setError("Couldn't prepare that configuration. Please try again.");
        return false;
      }
      const customization = await customizationResponse.json();
      await addItem(selectedProduct.id, customization.id, 1, options);
      return true;
    } catch {
      setError("Couldn't add that to your cart. Please try again.");
      return false;
    }
  };

  const handleAddToCart = async () => {
    setAdding(true);
    await buildAndAddToCart();
    setAdding(false);
  };

  const handleBuyNow = async () => {
    setAdding(true);
    // Heading straight to checkout -- popping the cart drawer open first would just flash and vanish
    // (worst on mobile, where it's a large fixed-position panel), so skip it here.
    const ok = await buildAndAddToCart({ openCartOnAdd: false });
    setAdding(false);
    if (ok) router.push("/checkout");
  };

  const canOrder = !!selectedProduct && (ignoreStock || selectedProduct.stockQuantity > 0);

  return (
    <div className="buy-layout">
      <ProductGallery
        key={`${colorId}-${stick}`} images={images} alt={`Basalt mount — ${selectedColor?.name ?? ""}`}
        onMainImageLoad={() => setCurrentImageReady(true)}
      />
      <ColorImagePreloader colorsToPreload={colorPreloadTargets} stick={stick} enabled={currentImageReady} />

      <div className="buy-config">
        <span className="word">BASALT MOUNT</span>
        {selectedProduct ? (
          <>
            <div className="price mono">
              {formatMoneyWithCode(selectedProduct.unitPrice, selectedProduct.currency)}
            </div>
            {!ignoreStock && (
              <span className="price-note">
                {selectedProduct.stockQuantity > 0 ? `${selectedProduct.stockQuantity} in stock` : "Out of stock"}
              </span>
            )}
          </>
        ) : (
          <>
            <div className="price mono">—</div>
            <span className="price-note">This fitment isn&apos;t available yet</span>
          </>
        )}

        <div className="buy-field">
          <span className="lbl">Which stick do you have?</span>
          <div className="pills">
            {STICKS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`pill${stick === s.id ? " sel" : ""}`}
                onClick={() => setStick(s.id)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Same collapsible-tile control as /customize's per-component pickers (customize-tile*
            classes, shared globals.css) -- explicit request to match that page's look, not a one-off
            style. Only one tile here (this tier applies a single color to every part, unlike
            /customize's one-per-component), still wrapped in .customize-tiles for the same vertical
            rhythm. */}
        <div className="customize-tiles">
          <div className={colorOpen ? "customize-tile open" : "customize-tile"}>
            <button type="button" className="customize-tile-header" onClick={() => setColorOpen((v) => !v)}>
              <span>Color</span>
              <span className="customize-tile-status">
                {selectedColor && <span className="admin-swatch-preview" style={{ background: selectedColor.swatchHex }} />}
                {selectedColor?.name ?? "Choose a color"}
              </span>
            </button>
            {colorOpen && (
              <div className="color-picker">
                {colors.map((c) => (
                  <button
                    key={c.id}
                    ref={(el) => registerSwatchEl(c.id, el)}
                    type="button"
                    className={`color-swatch${colorId === c.id ? " selected" : ""}`}
                    style={{ background: c.swatchHex }}
                    // Deliberately doesn't close the picker on selection (unlike /customize's per-part
                    // tiles, which auto-advance) -- explicit request: a shopper here is more likely
                    // browsing several colors in a row against the live sticky preview than making one
                    // final choice, so closing after the first pick would fight that. They close it
                    // themselves by tapping the header again, or just leave it open and scroll past.
                    onClick={() => setColorId(c.id)}
                    aria-label={c.name}
                    aria-pressed={colorId === c.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <Link href={`/customize?fitment=${stickMeta.fitment}`} className="buy-customize-link">
          One color&apos;s fine. Four is a personality. →
        </Link>

        <div className="kit-note">
          Includes the full 14–18mm pole mounting kit — nothing else to choose.
        </div>

        <ul className="buy-promo">
          <li>Rigid shell, rubber-lined contact points — built to survive impact</li>
          <li>Matte finish, all colorways</li>
          <li>Ships with every ring size — fit it to any pole on the mountain</li>
        </ul>

        {error && <p className="cart-page-error">{error}</p>}

        <div className="buy-actions">
          <button type="button" className="btn-secondary" onClick={handleAddToCart} disabled={!canOrder || adding}>
            {adding ? "Adding…" : "Add to Cart"}
          </button>
          <button type="button" className="btn-primary" onClick={handleBuyNow} disabled={!canOrder || adding}>
            {adding ? "Adding…" : "Buy Now"}
          </button>
        </div>
      </div>
    </div>
  );
}
